import React, { useCallback, useRef, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  SimpleGrid,
  Text,
  Tooltip,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  DndContext,
  closestCenter,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { motion, AnimatePresence } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import {
  MdAdd,
  MdArrowDownward,
  MdArrowDropDown,
  MdArrowUpward,
  MdCheck,
  MdClose,
  MdDelete,
  MdFilePresent,
  MdLock,
  MdMergeType,
  MdPictureAsPdf,
  MdRemove,
  MdUploadFile,
  MdVerticalAlignBottom,
  MdVerticalAlignTop,
} from 'react-icons/md';
import { usePdfMerge } from '../hooks/usePdfMerge';
import { mergePdfs } from '../utils/pdfExport';

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

const ZOOM_PRESETS = [25, 50, 75, 100, 125, 150];
const BASE_CARD_W = 160;

// ─── File tile (sortable) ─────────────────────────────────────────────────────

const FileTile = ({ item, isSelected, onSelect, onRemove }) => {
  const [isHovered, setIsHovered] = useState(false);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const sortableStyle = { transform: CSS.Transform.toString(transform), transition };

  return (
    <Box ref={setNodeRef} style={sortableStyle} opacity={isDragging ? 0.25 : 1} userSelect="none" {...attributes} {...listeners}>
      <Box
        bg={isSelected ? 'blue.50' : 'white'}
        borderWidth="2px"
        borderColor={isSelected ? 'blue.400' : isHovered ? 'gray.300' : 'gray.200'}
        boxShadow={isSelected ? '0 0 0 3px rgba(66,153,225,0.18), 0 4px 12px rgba(0,0,0,0.1)' : '0 1px 4px rgba(0,0,0,0.06)'}
        borderRadius="14px"
        overflow="visible"
        cursor={isDragging ? 'grabbing' : isHovered ? 'grab' : 'pointer'}
        position="relative"
        transition="border-color 0.15s, box-shadow 0.15s, background 0.15s"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => onSelect(item.id)}
        role="button"
        aria-pressed={isSelected}
      >
        {/* Selection circle */}
        <Box
          position="absolute" top={2} left={2} zIndex={4} w={5} h={5}
          borderRadius="full" borderWidth="2px"
          borderColor={isSelected ? 'blue.500' : 'blue.300'}
          bg={isSelected ? 'blue.500' : 'white'}
          display="flex" alignItems="center" justifyContent="center"
          opacity={isSelected || isHovered ? 1 : 0}
          transition="all 0.15s"
          pointerEvents={isSelected || isHovered ? 'auto' : 'none'}
          cursor="pointer"
          onClick={(e) => { e.stopPropagation(); onSelect(item.id); }}
          _hover={{ borderColor: 'blue.400', transform: 'scale(1.5)' }}
        >
          {isSelected && <Icon as={MdCheck} color="white" boxSize={3} />}
        </Box>

        {/* Delete button */}
        <Box position="absolute" top={1} right={1} zIndex={4} opacity={isHovered ? 1 : 0} transition="opacity 0.15s" pointerEvents={isHovered ? 'auto' : 'none'}>
          <Tooltip label="Remove" placement="top" hasArrow openDelay={400} fontSize="xs">
            <IconButton
              icon={<MdDelete size={22} />} aria-label="Remove file" size="sm" variant="ghost"
              colorScheme="red" borderRadius="lg" bg="whiteAlpha.800"
              transition="transform 0.15s, background 0.15s"
              _hover={{ bg: 'red.50', color: 'red.500', transform: 'scale(1.4)' }}
              onClick={(e) => { e.stopPropagation(); onRemove(item.id); }}
            />
          </Tooltip>
        </Box>

        {/* Thumbnail */}
        <Box mt={3} bg="gray.50" borderTopLeftRadius="12px" borderTopRightRadius="12px" overflow="hidden" display="flex" alignItems="center" justifyContent="center" minH="100px">
          {item.thumbnail ? (
            <img src={item.thumbnail} alt={`Preview of ${item.fileName}`} style={{ width: '100%', display: 'block' }} draggable={false} />
          ) : (
            <Icon as={MdFilePresent} boxSize={10} color="gray.300" my={6} />
          )}
        </Box>

        {/* File name + page count */}
        <Box px={2} pt={1.5} pb="6px">
          <Text as="span" display="block" fontSize="xs" fontWeight="700" color={isSelected ? 'blue.600' : 'gray.700'} noOfLines={1} title={item.fileName} lineHeight="1.3" transition="color 0.15s">
            {item.fileName.replace(/\.pdf$/i, '')}
          </Text>
          <Text as="span" display="block" fontSize="10px" color={isSelected ? 'blue.400' : 'gray.400'} lineHeight="1.4" mt="2px" transition="color 0.15s">
            {item.pageCount} {item.pageCount === 1 ? 'page' : 'pages'}
          </Text>
        </Box>
      </Box>
    </Box>
  );
};

// ─── Drag overlay ghost ───────────────────────────────────────────────────────

const DragOverlayTile = ({ item }) => (
  <Box bg="white" borderRadius="14px" borderWidth="2px" borderColor="blue.400" shadow="2xl" overflow="hidden" style={{ transform: 'rotate(2deg) scale(1.04)' }} opacity={0.95}>
    <Box mt={3} bg="blue.50" minH="100px" display="flex" alignItems="center" justifyContent="center">
      {item.thumbnail ? <img src={item.thumbnail} alt="" style={{ width: '100%', display: 'block' }} draggable={false} /> : <Icon as={MdFilePresent} boxSize={10} color="blue.200" my={6} />}
    </Box>
    <Box px={2} pt={1.5} pb="6px">
      <Text fontSize="xs" fontWeight="700" color="blue.600" noOfLines={1} lineHeight="1.3">{item.fileName.replace(/\.pdf$/i, '')}</Text>
      <Text fontSize="10px" color="blue.400" lineHeight="1.4" mt="2px">{item.pageCount} {item.pageCount === 1 ? 'page' : 'pages'}</Text>
    </Box>
  </Box>
);

// ─── Selection action bar ─────────────────────────────────────────────────────

const SelectionActionBar = ({ count, onMoveToFirst, onMoveUp, onMoveDown, onMoveToLast, onDelete, onClear }) => {
  const actionBtn = (label, BtnIcon, handler, scheme = 'gray') => (
    <Tooltip label={label} placement="top" hasArrow openDelay={400} fontSize="xs">
      <IconButton
        icon={<BtnIcon size={16} />} aria-label={label} size="xs" variant="ghost"
        colorScheme={scheme} borderRadius="lg" h={7} w={7} minW={7}
        _hover={{ bg: scheme === 'red' ? 'red.50' : 'gray.100' }}
        onClick={handler}
      />
    </Tooltip>
  );

  return (
    <MotionFlex
      initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.18, ease: 'easeOut' }}
      align="center" gap={1.5} px={3} py={2} bg="white"
      borderWidth="1px" borderColor="blue.100" borderRadius="14px"
      shadow="0 2px 12px rgba(66,153,225,0.1)" flexWrap="wrap" mb={4}
    >
      <Flex align="center" gap={2} flexShrink={0} mr={1}>
        <Box as="span" display="inline-flex" alignItems="center" justifyContent="center" minW={5} h={5} bg="blue.500" borderRadius="full" px={1.5}>
          <Text as="span" fontSize="10px" fontWeight="700" color="white">{count}</Text>
        </Box>
        <Text as="span" fontSize="xs" color="gray.500" fontWeight="500">
          {count === 1 ? 'file' : 'files'} selected
        </Text>
      </Flex>
      <Box w="1px" h={4} bg="gray.100" flexShrink={0} mx={0.5} />
      <HStack spacing={0}>
        {actionBtn('Move to First', MdVerticalAlignTop, onMoveToFirst)}
        {actionBtn('Move Up', MdArrowUpward, onMoveUp)}
        {actionBtn('Move Down', MdArrowDownward, onMoveDown)}
        {actionBtn('Move to Last', MdVerticalAlignBottom, onMoveToLast)}
      </HStack>
      <Box w="1px" h={4} bg="gray.100" flexShrink={0} mx={0.5} />
      {actionBtn('Delete selected', MdDelete, onDelete, 'red')}
      <Box w="1px" h={4} bg="gray.100" flexShrink={0} mx={0.5} />
      <Button size="xs" variant="ghost" colorScheme="gray" borderRadius="lg" leftIcon={<MdClose size={12} />} h={7} px={2.5} fontWeight="500" color="gray.500" _hover={{ bg: 'gray.100', color: 'gray.700' }} onClick={onClear} flexShrink={0}>
        Clear
      </Button>
    </MotionFlex>
  );
};

// ─── Upload empty state ───────────────────────────────────────────────────────

const UploadArea = ({ onDrop, isLoading }) => {
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true,
    disabled: isLoading,
    onDrop: (accepted) => { if (accepted.length) onDrop(accepted); },
  });

  return (
    <Flex flex={1} align="center" justify="center" p={8}>
      <Box
        {...getRootProps()}
        bg={isDragActive ? 'blue.50' : 'white'}
        borderWidth="2px" borderStyle="dashed"
        borderColor={isDragActive ? 'blue.400' : 'gray.200'}
        borderRadius="20px" p={{ base: 10, md: 14 }} textAlign="center"
        cursor={isLoading ? 'wait' : 'pointer'} transition="all 0.2s ease"
        shadow="sm" w="full" maxW="560px"
        _hover={isLoading ? {} : { borderColor: 'blue.300', bg: 'blue.50', shadow: 'md' }}
      >
        <input {...getInputProps()} />
        <VStack spacing={5}>
          <MotionBox animate={isDragActive ? { scale: 1.1 } : { scale: 1 }} transition={{ duration: 0.2 }}>
            <Box w={16} h={16} bg={isDragActive ? 'blue.100' : 'gray.100'} borderRadius="2xl" display="flex" alignItems="center" justifyContent="center" mx="auto" transition="background 0.2s">
              <Icon as={MdMergeType} boxSize={8} color={isDragActive ? 'blue.500' : 'gray.400'} transition="color 0.2s" />
            </Box>
          </MotionBox>
          <VStack spacing={1}>
            <Text fontWeight="700" fontSize="lg" color="gray.800">
              {isDragActive ? 'Release to add PDFs' : 'Drop your PDFs here'}
            </Text>
            <Text fontSize="sm" color="gray.400">Select multiple files — merged in the order you set</Text>
          </VStack>
          <Button colorScheme="blue" borderRadius="xl" px={8} fontWeight="600" isLoading={isLoading} loadingText="Loading…" onClick={(e) => { e.stopPropagation(); open(); }} pointerEvents={isLoading ? 'none' : 'auto'}>
            Select PDFs
          </Button>
          <Flex align="center" gap={1.5} color="gray.400">
            <Icon as={MdLock} boxSize={3} />
            <Text fontSize="xs">Processed locally in your browser · never uploaded</Text>
          </Flex>
        </VStack>
      </Box>
    </Flex>
  );
};

// ─── Main MergePdf component (content-only) ───────────────────────────────────

const MergePdf = () => {
  const {
    items, selectedIds, isLoadingFiles, loadErrors, totalPages,
    addFiles, removeItem, reorderItems, reset,
    toggleSelect, deselectAll, deleteSelected,
    moveUp, moveDown, moveToFirst, moveToLast,
  } = usePdfMerge();

  const [activeId, setActiveId] = useState(null);
  const [isMerging, setIsMerging] = useState(false);
  const [outputName, setOutputName] = useState('merged');
  const [zoom, setZoom] = useState(100);
  const addInputRef = useRef(null);
  const toast = useToast({ position: 'bottom-right' });

  const cardWidth = Math.round(BASE_CARD_W * zoom / 100);
  const cardMinWidth = cardWidth + 16;

  const zoomOut = () => {
    const idx = ZOOM_PRESETS.indexOf(zoom);
    if (idx > 0) setZoom(ZOOM_PRESETS[idx - 1]);
  };
  const zoomIn = () => {
    const idx = ZOOM_PRESETS.indexOf(zoom);
    if (idx < ZOOM_PRESETS.length - 1) setZoom(ZOOM_PRESETS[idx + 1]);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragStart = useCallback(({ active }) => setActiveId(active.id), []);
  const handleDragEnd = useCallback(({ active, over }) => {
    if (over && active.id !== over.id) reorderItems(active.id, over.id);
    setActiveId(null);
  }, [reorderItems]);

  const handleAddMore = () => addInputRef.current?.click();
  const handleAddMoreChange = (e) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length) addFiles(files);
    e.target.value = '';
  };

  // Whole-page drop zone (when files already loaded)
  const { getRootProps: getPageDropProps, getInputProps: getPageDropInput, isDragActive: isPageDragActive } = useDropzone({
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true, noClick: true,
    disabled: isLoadingFiles || isMerging,
    onDrop: (accepted) => { if (accepted.length) addFiles(accepted); },
  });

  const handleMerge = async () => {
    if (items.length < 2) return;
    setIsMerging(true);
    try {
      await mergePdfs(items, outputName || 'merged');
      toast({ title: 'Merged successfully', description: `${items.length} files · ${totalPages} pages combined.`, status: 'success', duration: 4000, isClosable: true });
    } catch (err) {
      toast({ title: 'Merge failed', description: err.message ?? 'Something went wrong.', status: 'error', duration: 5000, isClosable: true });
    } finally {
      setIsMerging(false);
    }
  };

  const activeDragItem = activeId ? items.find((p) => p.id === activeId) : null;

  // ── No files → upload state ─────────────────────────────────────────────────

  if (items.length === 0 && !isLoadingFiles) {
    return (
      <>
        <AnimatePresence>
          {loadErrors.length > 0 && (
            <MotionBox initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} px={6} pt={4}>
              {loadErrors.map((err, i) => (
                <Alert key={i} status="error" borderRadius="12px" mb={2} fontSize="sm">
                  <AlertIcon boxSize={4} />
                  <AlertDescription><Text as="span" fontWeight="600">{err.fileName}</Text>{' — '}{err.message}</AlertDescription>
                </Alert>
              ))}
            </MotionBox>
          )}
        </AnimatePresence>
        <UploadArea onDrop={addFiles} isLoading={isLoadingFiles} />
      </>
    );
  }

  // ── Files loaded → merge workspace ─────────────────────────────────────────

  return (
    <Flex flex={1} minH={0} {...getPageDropProps()} position="relative">
      <input {...getPageDropInput()} />

      {/* Whole-page drag overlay */}
      <AnimatePresence>
        {isPageDragActive && (
          <>
            <MotionBox position="fixed" inset={0} zIndex={50} bg="blue.500" opacity={0.08} initial={{ opacity: 0 }} animate={{ opacity: 0.08 }} exit={{ opacity: 0 }} pointerEvents="none" />
            <Box position="fixed" top="50%" left="50%" transform="translate(-50%, -50%)" zIndex={51} pointerEvents="none" textAlign="center">
              <Box bg="white" borderRadius="2xl" px={8} py={6} shadow="2xl" borderWidth="2px" borderColor="blue.300">
                <Icon as={MdMergeType} boxSize={10} color="blue.400" mb={2} />
                <Text fontWeight="700" fontSize="lg" color="blue.600">Drop to add PDFs</Text>
              </Box>
            </Box>
          </>
        )}
      </AnimatePresence>

      {/* ── Center column ── */}
      <Flex flex={1} flexDirection="column" minW={0}>

        {/* Selection action bar */}
        <AnimatePresence>
          {selectedIds.size > 0 && (
            <Box px={{ base: 4, md: 6 }} pt={4} bg="gray.50" position="sticky" top="64px" zIndex={9}>
              <SelectionActionBar
                count={selectedIds.size}
                onMoveToFirst={moveToFirst} onMoveUp={moveUp}
                onMoveDown={moveDown} onMoveToLast={moveToLast}
                onDelete={deleteSelected} onClear={deselectAll}
              />
            </Box>
          )}
        </AnimatePresence>

        {/* Scrollable grid area */}
        <Box flex={1} px={{ base: 4, md: 6 }} pt={4} pb={6} overflow="auto">
          {/* Load errors */}
          <AnimatePresence>
            {loadErrors.length > 0 && (
              <MotionBox initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} mb={4}>
                {loadErrors.map((err, i) => (
                  <Alert key={i} status="error" borderRadius="12px" mb={2} fontSize="sm">
                    <AlertIcon boxSize={4} />
                    <AlertDescription><Text as="span" fontWeight="600">{err.fileName}</Text>{' — '}{err.message}</AlertDescription>
                  </Alert>
                ))}
              </MotionBox>
            )}
          </AnimatePresence>

          {/* Controls row */}
          <Flex align="center" justify="space-between" mb={5} flexWrap="wrap" gap={3}>
            <Box />
            <HStack spacing={2}>
              {/* Zoom */}
              <HStack spacing={0}>
                <IconButton icon={<MdRemove size={14} />} aria-label="Zoom out" size="xs" variant="ghost" colorScheme="gray" borderRadius="md" isDisabled={zoom === ZOOM_PRESETS[0]} onClick={zoomOut} />
                <Menu placement="bottom-end">
                  <MenuButton as={Button} size="xs" variant="ghost" colorScheme="gray" rightIcon={<MdArrowDropDown size={14} />} minW="58px" fontWeight="500" fontSize="xs" px={2}>
                    {zoom}%
                  </MenuButton>
                  <MenuList minW="90px" py={1} shadow="lg" fontSize="sm">
                    {ZOOM_PRESETS.map((p) => (
                      <MenuItem key={p} fontWeight={zoom === p ? '600' : '400'} color={zoom === p ? 'blue.600' : 'gray.700'} onClick={() => setZoom(p)}>{p}%</MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                <IconButton icon={<MdAdd size={14} />} aria-label="Zoom in" size="xs" variant="ghost" colorScheme="gray" borderRadius="md" isDisabled={zoom === ZOOM_PRESETS[ZOOM_PRESETS.length - 1]} onClick={zoomIn} />
              </HStack>
              <Button leftIcon={<MdAdd size={15} />} size="xs" variant="outline" colorScheme="blue" borderRadius="full" fontWeight="600" isLoading={isLoadingFiles} onClick={handleAddMore}>
                Add PDFs
              </Button>
              <input ref={addInputRef} type="file" accept=".pdf,application/pdf" multiple style={{ display: 'none' }} onChange={handleAddMoreChange} />
            </HStack>
          </Flex>

          {/* Grid */}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <SortableContext items={items.map((p) => p.id)} strategy={rectSortingStrategy}>
              <SimpleGrid templateColumns={`repeat(auto-fill, ${cardMinWidth}px)`} spacing={4}>
                <AnimatePresence initial={false}>
                  {items.map((item) => (
                    <MotionBox key={item.id} layout initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.18 } }} transition={{ duration: 0.2 }} style={{ width: cardMinWidth }}>
                      <FileTile item={item} isSelected={selectedIds.has(item.id)} onSelect={toggleSelect} onRemove={removeItem} />
                    </MotionBox>
                  ))}
                </AnimatePresence>
              </SimpleGrid>
            </SortableContext>
            <DragOverlay dropAnimation={null}>
              {activeDragItem && <Box style={{ width: cardMinWidth }}><DragOverlayTile item={activeDragItem} /></Box>}
            </DragOverlay>
          </DndContext>

          {/* Drop more hint */}
          {!isPageDragActive && (
            <Flex align="center" justify="center" gap={2} mt={5} py={3} borderRadius="12px" borderWidth="1.5px" borderStyle="dashed" borderColor="gray.200" color="gray.400" fontSize="xs" cursor="pointer" transition="all 0.15s" _hover={{ borderColor: 'blue.300', color: 'blue.400', bg: 'blue.50' }} onClick={handleAddMore}>
              <MdUploadFile size={14} />
              <Text>Drop more PDFs here or click to select</Text>
            </Flex>
          )}
        </Box>
      </Flex>

      {/* ── Right panel ── */}
      <Box w="220px" minW="220px" bg="white" borderLeftWidth="1px" borderColor="gray.100" display={{ base: 'none', lg: 'flex' }} flexDirection="column" p={4} gap={4}>
        {/* Output name */}
        <Box>
          <Text fontSize="9px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.1em" mb={2}>Output file name</Text>
          <Flex align="center" gap={1.5}>
            <Input value={outputName} size="xs" variant="flushed" fontWeight="600" fontSize="sm" color="gray.700" borderColor="gray.200" _hover={{ borderColor: 'gray.400' }} _focus={{ borderColor: 'blue.500' }} onChange={(e) => setOutputName(e.target.value)} />
            <Text fontSize="xs" color="gray.400" flexShrink={0}>.pdf</Text>
          </Flex>
        </Box>

        <Divider borderColor="gray.100" />

        {/* Stats */}
        <Box>
          <Text fontSize="9px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.1em" mb={3}>Summary</Text>
          <VStack spacing={2} align="stretch">
            <Flex justify="space-between" align="center">
              <Text fontSize="xs" color="gray.500">Files</Text>
              <Text fontSize="xs" fontWeight="600" color="gray.700">{items.length}</Text>
            </Flex>
            <Flex justify="space-between" align="center">
              <Text fontSize="xs" color="gray.500">Total pages</Text>
              <Text fontSize="xs" fontWeight="600" color="gray.700">{totalPages}</Text>
            </Flex>
          </VStack>
        </Box>

        <Divider borderColor="gray.100" />

        {/* Actions */}
        <VStack spacing={2} align="stretch" mt="auto">
          {items.length === 1 && (
            <Text fontSize="10px" color="orange.500" textAlign="center" fontWeight="500">Add at least one more PDF to merge</Text>
          )}
          <Button leftIcon={<MdMergeType />} colorScheme="blue" borderRadius="lg" fontWeight="600" size="sm" w="full" isLoading={isMerging} loadingText="Merging…" isDisabled={items.length < 2 || isLoadingFiles} onClick={handleMerge}>
            Merge PDFs
          </Button>
          <Button variant="ghost" colorScheme="gray" borderRadius="lg" size="sm" w="full" onClick={reset} isDisabled={isMerging}>
            Clear all
          </Button>
        </VStack>

        <Box mt="auto">
          <Flex align="flex-start" gap={2} p={3} bg="gray.50" borderRadius="12px">
            <Icon as={MdPictureAsPdf} color="gray.300" boxSize={3.5} mt={0.5} flexShrink={0} />
            <Text fontSize="10px" color="gray.400" lineHeight="tall">All processing happens locally. Your files never leave this device.</Text>
          </Flex>
        </Box>
      </Box>

    </Flex>
  );
};

export default MergePdf;
